import { CircleInfoIcon } from "@xola/icons";
import { flash, Label, SubmitButton, Switch, Tooltip } from "@xola/ui-kit";
import {
    FormField,
    getAddonCatalog,
    getDemographicCatalog,
    integerAtleastZero,
    requiredNumberAtleastZero,
    TextareaField,
} from "common";
import { difference, omit } from "lodash";
import pluralize from "pluralize";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    AndFilter,
    ArrivalSpanFilter,
    CollectionNode,
    EqualsFilter,
    Experience,
    InScheduleFilter,
    LineItemCode,
    LineItemTemplate,
    MinimumTransitDurationFilter,
    Package,
    PackageSequences,
    PackageType,
    Permission,
    ProductCatalogItem,
    ProductObject,
    PurchaseRule,
    PurchaseRuleAction,
    PurchaseRuleFilterObject,
    PurchaseRuleLink,
    PurchaseRuleLinkEntityObject,
    PurchaseRuleShortcutType,
    SameDayArrivalFilter,
    Seller,
    ValidationLevel,
} from "types";
import { array, boolean, object, string } from "yup";
import { api } from "../../../../api/api";
import { humanizeEquipmentDuration } from "../../../../helpers/durationHelper";
import { ScrollToFieldError } from "../../../../helpers/validationHelper";
import { useSeller } from "../../../../hooks/useAccount";
import { useAutoFetchCollection } from "../../../../hooks/useAutoFetchCollection";
import { useCollection } from "../../../../hooks/useCollection";
import { useInvalidate } from "../../../../hooks/useInvalidation";
import { isAddonCatalogItem, isChoiceAddOn } from "../../../../models/CatalogItem";
import {
    getExistingPurchaseRule,
    getPurchaseRulesFromPurchaseRuleLink,
    getPurchaseRulesFromPurchaseRuleLinks,
} from "../../../../models/PurchaseRuleLink";
import {
    createAndFilter,
    createArrivalSpanFilter,
    createEqualsFilter,
    createInScheduleFilter,
    createMinimumTransitDurationFilter,
    createSameDayArrivalFilter,
} from "../../../../models/RuleFilter";
import { RadioInput } from "../../../Common/RadioInput";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { XolaFormik } from "../../../Common/XolaFormik";
import { InputField } from "../../../Field/InputField";
import { ContentLayout } from "../../../Layout/ContentLayout";
import { Loader } from "../../../Loader";
import { getProductCollectionPayload, ProductCollectionSelector } from "../../ProductCollectionSelector";
import { getPricingTermDiscount } from "../PackagePricingHelper";
import { PackageScheduleEditor } from "../PackageScheduleEditor";
import {
    BasicDiscountAddonTerm,
    BasicDiscountTerm,
    getCatalogByItem,
    getDefaultAction,
    getDefaultInsertAction,
    getPackageDiscountAction,
    getPackagePurchaseRuleLinkPayload,
    TermsType,
    useLineItemTemplateByCode,
} from "../PricingSelectorHelper";
import { MinimumDurationField } from "./MinimumDurationField";
import { PackageFormValues, StartRestrictionType, StartTimeRestriction } from "./StartTimeRestriction";
import { getEquipmentDisabledReasons } from "@/components/Common/ProductSelector";
import { ProductSelectorModal } from "@/components/Common/ProductSelectorModal";
import { SelectedProductListInput } from "@/components/Common/SelectedProductListInput";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useModal } from "@/hooks/useVisibility";
import { getProductIdsInPackage } from "@/models/Package";

const validationSchema = object().shape({
    name: string().required("is required"),
    excerpt: string().required("is required"),
    desc: string().required("is required"),
    type: string(),
    minimumTransitDuration: requiredNumberAtleastZero(
        "must be greater than or equal to 0",
        "should be positive integer",
        "should be positive integer",
    ).test({
        name: "checkMinimumTransitDuration",
        message:
            "Your duration between events can not be greater than your start time restriction (this would prevent all bookings!)",
        test() {
            const { minimumTransitDuration, restrictionDuration, showStartingRestriction, restrictionType } =
                this.parent;

            if (!showStartingRestriction) {
                return true;
            }

            return restrictionType === StartRestrictionType.SAME_DAY || minimumTransitDuration < restrictionDuration;
        },
    }),
    restrictionDuration: integerAtleastZero().test({
        name: "checkStartTimeRestriction",
        message:
            "Your start time restriction should be greater than 0 and can not be less than duration between events",
        test() {
            const { minimumTransitDuration, restrictionDuration, showStartingRestriction, restrictionType } =
                this.parent;

            if (!showStartingRestriction) {
                return true;
            }

            return restrictionType === StartRestrictionType.SAME_DAY || minimumTransitDuration < restrictionDuration;
        },
    }),
    selectedExperiences: object().shape({
        all: boolean(),
        selected: array().when("all", {
            is: false,
            then: array().min(1, "are required"),
        }),
    }),
});

export const PackageDescriptiveContent = ({ pkg }: { pkg?: Package }) => {
    const history = useHistory();
    const invalidate = useInvalidate();
    const seller = useSeller();
    const collectionModal = useModal();
    const [isProductSelectorOpen, setIsProductSelectorOpen] = useState(false);

    const { isGranted } = useAuthorization();
    const showCollectionModification = isGranted(Permission.MODIFY_COLLECTIONS);

    const { data: addedCollections, isLoading: isLoadingAddedCollections } = useAutoFetchCollection<CollectionNode>(
        "collectionNodes",
        pkg?.id ? { product: pkg.id } : undefined,
        { enabled: !!pkg?.id && showCollectionModification },
    );
    const addedCollectionPaths = addedCollections.map((node) => node.path ?? "").filter(Boolean);

    const { data: experiences, isFetching } = useAutoFetchCollection<Experience>("experiences");

    const equipmentDisabledReasons = getEquipmentDisabledReasons(experiences);

    const { data: discountTemplate, isLoading: isLoadingDiscountTemplate } = useLineItemTemplateByCode(
        LineItemCode.PACKAGE_DISCOUNT,
    );

    const { data: purchaseRuleLinks, isLoading: isLoadingPurchaseRuleLink } = useCollection<PurchaseRuleLink>(
        "purchaseRuleLinks",
        {
            "entities.id": pkg?.id,
            "entities.object": PurchaseRuleLinkEntityObject.PACKAGE,
            expand: "purchaseRule",
        },
        {
            enabled: !!pkg,
        },
    );

    const initialPurchaseRule: PurchaseRule[] = [
        {
            name: "",
            actions: [],
            seller: { id: seller.id },
        },
    ];

    const [purchaseRuleState, setPurchaseRuleState] = useState<PurchaseRule[]>(initialPurchaseRule);

    useEffect(() => {
        const purchaseRules = getPurchaseRulesFromPurchaseRuleLinks(purchaseRuleLinks);
        if (purchaseRules.length > 0) {
            setPurchaseRuleState(purchaseRules);
        }
    }, [purchaseRuleLinks]);

    const purchaseRuleStateIndex = purchaseRuleState.length - 1;
    const transitDurationInMinutes = getMinimumDuration(purchaseRuleState[purchaseRuleStateIndex]) ?? 0;
    const startRestriction = getStartRestriction(purchaseRuleState[purchaseRuleStateIndex]);
    const sameDayRestriction = getSameDayRestriction(purchaseRuleState[purchaseRuleStateIndex]);

    const initialValues: PackageFormValues = {
        name: "",
        desc: "",
        excerpt: "",
        type: PackageType.DEMOGRAPHICS_VARIES,
        seller: { id: seller.id },
        selectedExperiences: { all: false, selected: pkg ? getProductIdsInPackage(pkg) : [] },
        ...pkg,
        minimumTransitDuration: transitDurationInMinutes,
        showStartingRestriction: !!startRestriction || !!sameDayRestriction,
        restrictionDuration: startRestriction ?? 0,
        schedules: pkg?.schedules ?? [],
        restrictionType: sameDayRestriction ? StartRestrictionType.SAME_DAY : StartRestrictionType.WITH_IN,
        collections: addedCollectionPaths,
    };

    const { mutateAsync: savePackage } = useMutation(async (payload: Partial<PackageFormValues>) => {
        try {
            if (pkg?.id) {
                const { data: savedPackage } = await api.put<Package>(`/packages/${pkg?.id}`, payload);
                return savedPackage;
            }

            const { data: savedPackage } = await api.post<Package>("/packages", payload);
            return savedPackage;
        } catch {
            flash.danger("Error saving package");
        }
    });

    const { mutateAsync: savePurchaseRule } = useMutation(async (purchaseRuleState: PurchaseRule) => {
        if (purchaseRuleState?.id) {
            const { data: savedPurchaseRule } = await api.put<PurchaseRule>(
                `/purchaseRules/${purchaseRuleState.id}`,
                purchaseRuleState,
            );
            return savedPurchaseRule;
        }

        const { data: savedPurchaseRule } = await api.post<PurchaseRule>(`/purchaseRules`, purchaseRuleState);
        return savedPurchaseRule;
    });

    const { mutateAsync: savePurchaseRuleLink } = useMutation(async (purchaseRuleLink: PurchaseRuleLink) => {
        if (purchaseRuleLink?.id) {
            const { id } = purchaseRuleLink;
            return api.put<PurchaseRuleLink>(`/purchaseRuleLinks/${id}`, purchaseRuleLink);
        }

        return api.post<PurchaseRuleLink>(`/purchaseRuleLinks`, purchaseRuleLink);
    });

    const { mutateAsync: deletePurchaseRule } = useMutation(async (purchaseRuleState?: PurchaseRule) => {
        if (purchaseRuleState?.id) {
            await api.delete<PurchaseRule>(`/purchaseRules/${purchaseRuleState.id}`);
        }
    });

    const { mutateAsync: deletePurchaseRuleLink } = useMutation(async (purchaseRuleLink?: PurchaseRuleLink) => {
        if (purchaseRuleLink?.id) {
            return api.delete<PurchaseRuleLink>(`/purchaseRuleLinks/${purchaseRuleLink.id}`);
        }
    });

    const { mutateAsync: saveCollections } = useMutation(
        async ({ newPaths, packageId }: { newPaths: string[]; packageId: string }) => {
            const collectionUpdateOperations = getProductCollectionPayload({
                newPaths,
                oldPaths: addedCollectionPaths,
                addedCollections,
                productId: packageId,
            });
            if (collectionUpdateOperations.length > 0) {
                await api.patch("/collectionNodes", { ops: collectionUpdateOperations });
                invalidate("collectionNodes");
            }
        },
    );

    const handleCancel = () => {
        history.push(`/products/packages`);
    };

    const handleSubmit = async (values: PackageFormValues) => {
        if (!discountTemplate) {
            flash.danger("Package discounts are not set up for this account. Please contact Xola support.");
            return null;
        }

        const savedPackage = await savePackage(getPackagePayload(values));

        if (!savedPackage?.id) {
            return null;
        }

        const selectedProductIds = pkg ? getProductIdsInPackage(pkg) : [];
        if (pkg && values.selectedProducts && values.selectedExperiences.selected !== selectedProductIds) {
            const unselectedProductIds = difference(selectedProductIds, values.selectedExperiences.selected);
            for (const productId of unselectedProductIds) {
                const purchaseRule = purchaseRuleState.find((rule) => rule.meta?.subProductId === productId);
                const purchaseRuleLink = getExistingPurchaseRule(purchaseRuleLinks, purchaseRule);
                await deletePurchaseRule(purchaseRule);
                await deletePurchaseRuleLink(purchaseRuleLink);
            }
        }

        let existingPurchaseRuleLink = null;
        if (savedPackage.selectedProducts) {
            for (const selectedProduct of savedPackage.selectedProducts) {
                const experience = experiences.find(({ id }) => selectedProduct.product.id === id);
                if (experience) {
                    const purchaseRule = purchaseRuleState.find((rule) => rule.meta?.subProductId === experience.id);
                    const purchaseRuleLink = getExistingPurchaseRule(purchaseRuleLinks, purchaseRule);
                    let newPurchaseRule = null;
                    if (existingPurchaseRuleLink) {
                        const discountTerm = getPricingTermDiscount(purchaseRuleState, [existingPurchaseRuleLink]);

                        if (discountTerm.termType === TermsType.BASIC && !purchaseRule) {
                            const existingRules = getPurchaseRulesFromPurchaseRuleLink(existingPurchaseRuleLink);
                            const existingRule = existingRules[0];
                            const discount = existingRule.actions[1]?.shortcut?.amount ?? 0;
                            const term = getBasicDiscountTerm(discount, discountTerm);
                            const filter = getPurchaseRuleFilter(values, savedPackage.id, experience.id);
                            newPurchaseRule = createBasicPurchaseRule(
                                savedPackage,
                                experience,
                                term,
                                filter,
                                seller,
                                discountTemplate,
                            );
                        }
                    }

                    if (purchaseRuleLink && !existingPurchaseRuleLink) {
                        existingPurchaseRuleLink = purchaseRuleLink;
                    }

                    if (!newPurchaseRule) {
                        newPurchaseRule = purchaseRule
                            ? {
                                  ...purchaseRule,
                                  filter: getPurchaseRuleFilter(values, savedPackage.id, selectedProduct.product.id),
                              }
                            : createNewRule(values, savedPackage, experience, seller, discountTemplate);
                    }

                    const savedRule = await savePurchaseRule(newPurchaseRule);

                    const purchaseRuleLinkPayload = getPackagePurchaseRuleLinkPayload(
                        seller,
                        savedPackage,
                        savedRule,
                        purchaseRuleLink,
                        undefined,
                        existingPurchaseRuleLink,
                    );

                    await savePurchaseRuleLink(purchaseRuleLinkPayload);
                    invalidate("purchaseRuleLinks", purchaseRuleLink?.id);
                }
            }

            invalidate("packages", pkg?.id);
        }

        const hasCollectionChanges =
            showCollectionModification &&
            values.collections &&
            Array.isArray(values.collections) &&
            JSON.stringify([...values.collections].sort()) !== JSON.stringify([...addedCollectionPaths].sort());

        if (hasCollectionChanges) {
            await saveCollections({ newPaths: values.collections!, packageId: savedPackage.id });
        }

        if (pkg?.id) {
            flash.success(`Successfully updated package ${pkg.name}`);
        } else {
            history.push(`/products/packages/${savedPackage.id}/pricing`);
        }
    };

    if (isLoadingPurchaseRuleLink || isFetching || isLoadingDiscountTemplate) {
        return <Loader />;
    }

    return (
        <ContentLayout isWide>
            <XolaFormik
                enablePrompt
                enableReinitialize
                validateOnChange
                validateOnBlur={false}
                validationSchema={validationSchema}
                initialValues={initialValues}
                onSubmit={handleSubmit}
            >
                {({ values, isSubmitting, handleSubmit, setFieldValue, errors, submitCount }) => {
                    const selectedExperiencesError = submitCount > 0 && !!errors.selectedExperiences;

                    return (
                        <>
                            <h2 className="mt-5">Package description</h2>
                            <div className="divide-y divide-gray-light">
                                <ContentLayout.Section
                                    title="Basic Information"
                                    description="Important details that will be presented to your customer throughout the booking process."
                                >
                                    {showCollectionModification && (
                                        <FormField name="collections" label="">
                                            <ProductCollectionSelector
                                                addedCollectionPaths={addedCollectionPaths}
                                                isLoading={isLoadingAddedCollections}
                                                value={values.collections ?? []}
                                                isOpen={collectionModal.isVisible}
                                                onChange={(collections: string[]) => {
                                                    setFieldValue("collections", collections);
                                                }}
                                                onOpenChange={(isOpen) =>
                                                    isOpen ? collectionModal.show() : collectionModal.hide()
                                                }
                                            />
                                        </FormField>
                                    )}
                                    <FormField name="name" label="Title">
                                        <InputField isRequired />
                                    </FormField>

                                    <FormField
                                        name="excerpt"
                                        label="Excerpt"
                                        tooltip="This text is shown to your customers during the online checkout process. It does not appear in the confirmation email"
                                    >
                                        <TextareaField isRequired rows={6} />
                                    </FormField>

                                    <FormField
                                        name="desc"
                                        label="Description"
                                        tooltip="This text is for participation in optional distribution channels. It does NOT appear to your customers during checkout or in the confirmation email"
                                    >
                                        <TextareaField isRequired rows={6} />
                                    </FormField>
                                </ContentLayout.Section>

                                <ContentLayout.Section
                                    title="Products & Restrictions"
                                    description="Select which products are part of this package as well as restrictions that apply."
                                >
                                    <div className="mb-5">
                                        <Label>Demographic Restrictions</Label>
                                        <RadioInput
                                            name="type"
                                            checked={values.type === PackageType.DEMOGRAPHICS_VARIES}
                                            disabled={!!pkg}
                                            onChange={() => {
                                                setFieldValue("type", PackageType.DEMOGRAPHICS_VARIES);
                                            }}
                                        >
                                            Demographics may vary across products in package
                                        </RadioInput>
                                        <RadioInput
                                            name="type"
                                            checked={values.type === PackageType.DEMOGRAPHICS_SHARED}
                                            disabled={!!pkg}
                                            onChange={() => {
                                                setFieldValue("selectedExperiences", { all: false, selected: [] });
                                                setFieldValue("type", PackageType.DEMOGRAPHICS_SHARED);
                                            }}
                                        >
                                            All products in the package must share common demographics
                                        </RadioInput>
                                    </div>

                                    <div className="mb-4 space-y-2">
                                        <SelectedProductListInput
                                            showNoProductCard
                                            showDragAndDropProductList
                                            labelTooltipContent="Select products that are included in this package"
                                            error={
                                                selectedExperiencesError ? errors.selectedExperiences?.selected : null
                                            }
                                            selectedProducts={values.selectedExperiences}
                                            onEditClick={() => setIsProductSelectorOpen(true)}
                                            onChange={(selectionState) =>
                                                setFieldValue("selectedExperiences", selectionState)
                                            }
                                        />

                                        <ProductSelectorModal
                                            hideDraft
                                            hideUnScheduled
                                            disablePerOutingPrice
                                            title="Product Restrictions"
                                            allowOnlyCommonDemographic={values.type === PackageType.DEMOGRAPHICS_SHARED}
                                            enableSelectAll={false}
                                            value={values.selectedExperiences}
                                            objects={[ProductObject.EXPERIENCE]}
                                            isOpen={isProductSelectorOpen}
                                            disabledReasons={equipmentDisabledReasons}
                                            onConfirm={(selectionState) =>
                                                setFieldValue("selectedExperiences", selectionState)
                                            }
                                            onClose={() => setIsProductSelectorOpen(false)}
                                        />
                                    </div>

                                    <StartTimeRestriction />

                                    <div className="mb-5">
                                        <Label>
                                            Blackout schedule{" "}
                                            <Tooltip
                                                placement="auto"
                                                content="Packages inherit their schedule of availability from the products that make them up. You can prevent packaged bookings from occurring by adding a blackout schedule."
                                            >
                                                <CircleInfoIcon />
                                            </Tooltip>
                                        </Label>
                                        <PackageScheduleEditor
                                            title="Schedule Settings"
                                            description="The availability schedule for this Package is derived from the Products that make up this Package. Use Blackout Schedules to make the Package unavailable for booking on specific dates. If a product’s schedule has a price change built in, that price change will take effect within that schedule."
                                            blackoutSchedules={values.schedules}
                                            onBlackoutSchedulesChange={(schedules) =>
                                                setFieldValue("schedules", schedules)
                                            }
                                        />
                                    </div>

                                    <MinimumDurationField />
                                </ContentLayout.Section>

                                <ContentLayout.Section
                                    title="Conditions"
                                    description="Include other important information important for your customers to see."
                                >
                                    <FormField
                                        name="cancellation.policy"
                                        label="Cancellation Policy"
                                        tooltip="This will be included in the booking confirmation and reminder emails. If your customer purchases Refundable booking, this cancellation policy will be replaced with the refund process offered by the provider."
                                    >
                                        <TextareaField rows={6} />
                                        <div className="mt-2 flex space-x-2">
                                            <Switch
                                                size="medium"
                                                isChecked={values.cancellation?.refundable ?? false}
                                                onChange={(value: boolean) => {
                                                    setFieldValue("cancellation", {
                                                        ...values.cancellation,
                                                        refundable: value,
                                                    });
                                                }}
                                            />
                                            <span>
                                                My cancellation policy offers refunds, store credits, and/or date
                                                changes
                                            </span>
                                        </div>
                                    </FormField>

                                    <FormField
                                        name="other"
                                        label="Other Considerations"
                                        tooltip="This text is added to your confirmation and reminder emails to customers"
                                    >
                                        <TextareaField rows={6} />
                                    </FormField>
                                </ContentLayout.Section>

                                <ContentLayout.Footer preferenceSidebar>
                                    <SubmitButton
                                        isLoading={isSubmitting}
                                        disabled={isSubmitting}
                                        onClick={handleSubmit}
                                    >
                                        {pkg ? "Save" : "Next"}
                                    </SubmitButton>

                                    <SecondaryButton type="reset" onClick={handleCancel}>
                                        Cancel
                                    </SecondaryButton>
                                </ContentLayout.Footer>

                                <ScrollToFieldError />
                            </div>
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

const getBasicDiscountTerm = (discount: number, discountTerm: BasicDiscountTerm): BasicDiscountTerm => {
    return {
        termType: TermsType.BASIC,
        discount: discount,
        includeAddons: discountTerm.includeAddons,
    };
};

const getPackagePayload = (values: PackageFormValues) => {
    return {
        ...omit(
            values,
            "selectedExperiences",
            "collections",
            "durationUnit",
            "showStartingRestriction",
            "restrictionDuration",
            "restrictionType",
        ),
        selectedProducts: values.selectedExperiences.selected.map((experienceId, index) => {
            return {
                product: { id: experienceId },
                sequence: (index + 1) * PackageSequences.SELECTED_PRODUCTS_SEQUENCE_START, //pass the sorted Selected Product with generated sequence number
            };
        }),
    };
};

const getMinimumDuration = (purchaseRule: PurchaseRule): number | null => {
    if (!purchaseRule) {
        return null;
    }

    if (purchaseRule.filter?.object !== PurchaseRuleFilterObject.AND_FILTER) {
        return null;
    }

    const minimumDurationFilters = purchaseRule.filter?.filters.filter((filter) => {
        return filter.object === "minimum_transit_duration_filter";
    }) as MinimumTransitDurationFilter[];

    return minimumDurationFilters.length > 0 ? minimumDurationFilters[0].operand : null;
};

const getStartRestriction = (purchaseRule: PurchaseRule): number | null => {
    if (!purchaseRule) {
        return null;
    }

    if (purchaseRule.filter?.object !== PurchaseRuleFilterObject.AND_FILTER) {
        return null;
    }

    const startRestrictionFilters = purchaseRule.filter?.filters.filter((filter) => {
        return filter.object === "arrival_span_filter";
    }) as ArrivalSpanFilter[];

    return startRestrictionFilters.length > 0 ? startRestrictionFilters[0].operand : null;
};

const getSameDayRestriction = (purchaseRule: PurchaseRule): SameDayArrivalFilter | null => {
    if (!purchaseRule) {
        return null;
    }

    if (purchaseRule.filter?.object !== PurchaseRuleFilterObject.AND_FILTER) {
        return null;
    }

    const sameDayRestrictionFilters = purchaseRule.filter?.filters.filter((filter) => {
        return filter.object === "same_day_arrival_filter";
    }) as SameDayArrivalFilter[];

    return sameDayRestrictionFilters.length > 0 ? sameDayRestrictionFilters[0] : null;
};

const getPurchaseRuleFilter = (values: PackageFormValues, packageId: ID, experienceId: ID): AndFilter => {
    const filters: Array<
        InScheduleFilter | EqualsFilter | MinimumTransitDurationFilter | ArrivalSpanFilter | SameDayArrivalFilter
    > = [];

    const {
        schedules,
        selectedExperiences,
        minimumTransitDuration,
        showStartingRestriction,
        restrictionDuration,
        restrictionType,
    } = values;

    const filterSchedules = schedules.filter((schedule) => {
        return !schedule._remove;
    });

    if (filterSchedules.length > 0) {
        filters.push(
            createInScheduleFilter({
                schedules: filterSchedules,
                field: "arrivalDateTime",
                validationLevel: ValidationLevel.ERROR,
            }),
        );
    }

    if (selectedExperiences?.selected) {
        filters.push(createEqualsFilter(packageId, "parentProduct.id"), createEqualsFilter(experienceId, "product.id"));
    }

    if (minimumTransitDuration > 0) {
        const transitDuration = humanizeEquipmentDuration(minimumTransitDuration);
        const errorMessage = `Duration between all items should be greater than ${transitDuration.duration} ${pluralize(
            transitDuration.durationUnit,
            transitDuration.duration,
        )}`;
        filters.push(createMinimumTransitDurationFilter({ operand: minimumTransitDuration, errorMessage }));
    }

    if (showStartingRestriction) {
        if (restrictionType === StartRestrictionType.SAME_DAY) {
            filters.push(createSameDayArrivalFilter({}));
        } else {
            const humanizedDuration = humanizeEquipmentDuration(restrictionDuration);

            const errorMessage = `Arrival of all items should be within ${humanizedDuration.duration} ${pluralize(
                humanizedDuration.durationUnit,
                humanizedDuration.duration,
            )}`;
            filters.push(createArrivalSpanFilter({ operand: restrictionDuration, errorMessage }));
        }
    }

    return createAndFilter({ filters });
};

const createBasicPurchaseRule = (
    pkg: Package,
    experience: Experience,
    basicDiscount: BasicDiscountTerm,
    filter: AndFilter,
    seller: Seller,
    discountTemplate: LineItemTemplate | null,
) => {
    const discountPricingValues: PurchaseRuleAction[] = [];
    const catalogs = [...getDemographicCatalog(experience), ...getAddonCatalog(experience)];
    const shouldIncludeAddons = basicDiscount.includeAddons === BasicDiscountAddonTerm.INCLUDING_ADD_ONS;

    populateDiscountAction(catalogs, discountPricingValues, basicDiscount, discountTemplate);
    return {
        filter: filter,
        meta: { product: pkg.id, subProductId: experience.id, addons: shouldIncludeAddons },
        name: `Purchase rule for package ${pkg?.name} experience ${experience?.name}`,
        actions: [getDefaultInsertAction(discountTemplate), ...discountPricingValues],
        seller: { id: seller.id },
        processActionsForIgnoredValidations: true,
    };
};

const populateDiscountAction = (
    catalogs: ProductCatalogItem[],
    discountPricingValue: PurchaseRuleAction[],
    basicDiscount: BasicDiscountTerm,
    discountTemplate: LineItemTemplate | null,
) => {
    for (const catalog of catalogs) {
        const catalogItem = getCatalogByItem(catalogs, catalog) ?? catalog;
        const shouldExcludeAddons = basicDiscount.includeAddons === BasicDiscountAddonTerm.EXCLUDING_ADD_ONS;
        const discount = isAddonCatalogItem(catalogItem) && shouldExcludeAddons ? 0 : basicDiscount.discount;

        if (isChoiceAddOn(catalogItem)) {
            populateDiscountAction(
                catalog.items as ProductCatalogItem[],
                discountPricingValue,
                basicDiscount,
                discountTemplate,
            );
        } else {
            const action = getDefaultAction();
            const value = {
                shortcutType: PurchaseRuleShortcutType.PERCENT,
                discount: -1 * discount,
            };
            const discountAction = getPackageDiscountAction(value, action, catalogItem, discountTemplate);

            discountPricingValue.push(discountAction);
        }
    }
};

const createNewRule = (
    values: PackageFormValues,
    pkg: Package,
    product: Experience,
    seller: Seller,
    discountTemplate: LineItemTemplate | null,
): PurchaseRule => {
    return {
        filter: getPurchaseRuleFilter(values, pkg.id, product.id),
        meta: { product: pkg.id, subProductId: product.id },
        name: `Purchase rule for package ${pkg?.name} experience ${product?.name}`,
        actions: [getDefaultInsertAction(discountTemplate)],
        seller: { id: seller.id },
        processActionsForIgnoredValidations: true,
        shouldCheckFieldValueForIgnoredValidations: true,
    };
};
